import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const MAX_STARS = 6;

export function useProjectStars() {
  const [starredIds, setStarredIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchStars = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data, error } = await supabase
        .from('project_stars' as any)
        .select('project_id')
        .eq('user_id', user.id);

      if (!error && data) {
        setStarredIds(new Set((data as any[]).map((s: any) => s.project_id)));
      }
      setLoading(false);
    };
    fetchStars();
  }, []);

  const toggleStar = useCallback(async (projectId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const isStarred = starredIds.has(projectId);

    if (!isStarred && starredIds.size >= MAX_STARS) {
      toast({
        title: 'Maximum bereikt',
        description: `Je kunt maximaal ${MAX_STARS} projecten als focus markeren.`,
        variant: 'destructive',
      });
      return;
    }

    // Optimistic update
    setStarredIds(prev => {
      const next = new Set(prev);
      if (isStarred) next.delete(projectId);
      else next.add(projectId);
      return next;
    });

    if (isStarred) {
      const { error } = await (supabase.from('project_stars' as any) as any)
        .delete()
        .eq('project_id', projectId)
        .eq('user_id', user.id);
      if (error) {
        // Revert
        setStarredIds(prev => { const next = new Set(prev); next.add(projectId); return next; });
      }
    } else {
      const { error } = await (supabase.from('project_stars' as any) as any)
        .insert({ project_id: projectId, user_id: user.id });
      if (error) {
        setStarredIds(prev => { const next = new Set(prev); next.delete(projectId); return next; });
      }
    }
  }, [starredIds, toast]);

  return { starredIds, toggleStar, loading };
}
